import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Factory, Package, Crown, Layers, Rocket, MessageCircle, ArrowRight, Sparkles, Droplets, Scissors, HeartPulse, Gem, CheckCircle2 } from 'lucide-react';
import Seo from '../components/Seo';
import useReveal from '../hooks/useReveal';
import { useLang } from '../context/LanguageContext';
import { IMAGES } from '../mock/mock';
import { WHATSAPP_URL, track } from '../components/landingUtils';
import { getLandingCopy } from '../i18n/landing';
import CaseStudies from '../components/CaseStudies';

const BENEFIT_ICONS = [Package, FlaskConical, Factory, Layers, Crown, Rocket];
const CAT_META = [
  { icon: Droplets, img: IMAGES.skincare },
  { icon: Scissors, img: IMAGES.personalCare },
  { icon: HeartPulse, img: IMAGES.sunCare },
  { icon: Gem, img: IMAGES.serumSplash },
];

const Kicker = ({ children, light }) => (
  <span className={`inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase ${light ? 'text-gold-light' : 'text-gold'}`}>
    <span className="w-8 h-px bg-gold" />{children}<span className="w-8 h-px bg-gold" />
  </span>
);

export default function PrivateLabel() {
  const { lang } = useLang();
  const L = getLandingCopy(lang);
  const c = L.pl, sh = L.shared;
  useReveal(lang);
  useEffect(() => { track('landing_page_view', { page: 'private-label-cosmetics' }); }, []);

  return (
    <div>
      <Seo title="Private Label Cosmetics Manufacturer UK"
        description="Launch your own premium cosmetics brand with ARGE Cosmetics. Private label skincare, haircare and beauty products with formulation, manufacturing, filling, packaging and turnkey support."
        path="/private-label-cosmetics"
        jsonLd={{ '@context': 'https://schema.org', '@type': 'Service', name: 'Private Label Cosmetics Manufacturing', provider: { '@type': 'Organization', name: 'ARGE Cosmetics', url: 'https://argecosmetics.co.uk' }, areaServed: 'GB', serviceType: 'Private label cosmetics manufacturing' }} />

      <section className="relative min-h-[88vh] flex items-center pt-32 pb-20 overflow-hidden bg-cream">
        <img src={IMAGES.heroBottle} alt="Premium private label skincare products manufactured by ARGE Cosmetics" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/85 to-cream/20" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
          <div className="max-w-2xl">
            <Kicker>{c.kicker}</Kicker>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-espresso leading-[1.08]">{c.h1}</h1>
            <p className="mt-6 text-lg text-mocha leading-relaxed max-w-xl">{c.sub}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact" onClick={() => track('start_brand_click', { page: 'private-label' })} data-testid="pl-hero-primary-cta" className="gold-sheen inline-flex items-center gap-3 px-10 py-4 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors">{c.cta1} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></Link>
              <Link to="/contact" onClick={() => track('quote_request', { page: 'private-label' })} data-testid="pl-hero-secondary-cta" className="inline-flex items-center gap-3 px-10 py-4 border border-espresso/30 text-espresso text-xs tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors">{sh.quote}</Link>
            </div>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" onClick={() => track('whatsapp_click', { page: 'private-label' })} data-testid="pl-whatsapp-link" className="mt-6 inline-flex items-center gap-2 text-sm text-mocha hover:text-gold transition-colors"><MessageCircle className="w-4 h-4 text-gold" /> {sh.wa}</a>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker>{c.whyK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl text-espresso mt-4 max-w-3xl mx-auto">{c.whyT}</h2>
            <p className="mt-5 text-mocha max-w-2xl mx-auto">{c.whyP}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.benefits.map((b, i) => {
              const Icon = BENEFIT_ICONS[i];
              return (
                <div key={b.t} className="reveal bg-cream p-8 border-t-2 border-gold hover:-translate-y-1 transition-transform duration-300">
                  <Icon className="w-7 h-7 text-gold mb-5" />
                  <h3 className="font-serif text-2xl text-espresso">{b.t}</h3>
                  <p className="mt-3 text-sm text-mocha leading-relaxed">{b.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker>{c.capK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl text-espresso mt-4">{c.capT}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.cats.map((cat, i) => {
              const meta = CAT_META[i];
              return (
                <div key={cat.t} className="reveal group bg-ivory overflow-hidden">
                  <div className="h-44 overflow-hidden">
                    <img src={meta.img} alt={cat.t} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-2 mb-4"><meta.icon className="w-5 h-5 text-gold" /><h3 className="font-serif text-2xl text-espresso">{cat.t}</h3></div>
                    <ul className="space-y-1.5">
                      {cat.items.map((it) => <li key={it} className="text-sm text-mocha flex items-center gap-2"><span className="w-1 h-1 bg-gold rounded-full" />{it}</li>)}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-espresso text-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker light>{c.procK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl mt-4">{c.procT}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {c.steps.map((s, i) => (
              <div key={s.t} className="reveal border-l border-gold/40 pl-6">
                <p className="font-serif text-5xl text-gold/50">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-serif text-2xl mt-3 text-gold-light uppercase tracking-wide">{s.t}</h3>
                <p className="mt-2 text-sm text-ivory/75 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-ivory">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="reveal">
            <Kicker>{c.whoK}</Kicker>
            <h2 className="font-serif text-3xl md:text-4xl text-espresso mt-4">{c.whoT}</h2>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3 reveal">
            {c.clients.map((cl) => (
              <span key={cl} className="px-6 py-3 bg-cream border border-gold-soft text-sm text-espresso tracking-wide hover:border-gold transition-colors">{cl}</span>
            ))}
          </div>
          <p className="mt-10 text-mocha text-sm reveal">{c.crossPre}<Link to="/contract-cosmetics-manufacturing" className="text-gold underline underline-offset-4 hover:text-espresso transition-colors">{c.crossL1}</Link>{c.crossMid}<Link to="/influencer-beauty-brand" className="text-gold underline underline-offset-4 hover:text-espresso transition-colors">{c.crossL2}</Link>{c.crossPost}</p>
        </div>
      </section>

      <CaseStudies />

      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker>{c.modK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl text-espresso mt-4">{c.modT}</h2>
            <p className="mt-5 text-mocha max-w-2xl mx-auto">{c.modP}</p>
          </div>
          <div className="space-y-4">
            {c.models.map((m) => (
              <div key={m.t} className="reveal flex flex-col sm:flex-row gap-3 sm:gap-10 bg-ivory p-7 border-l-2 border-gold">
                <h3 className="font-serif text-2xl text-espresso sm:w-64 shrink-0">{m.t}</h3>
                <p className="text-sm text-mocha leading-relaxed self-center">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-espresso text-center">
        <div className="max-w-3xl mx-auto px-6 reveal">
          <Sparkles className="w-8 h-8 text-gold-light mx-auto mb-5" />
          <h2 className="font-serif text-4xl md:text-5xl text-ivory">{c.finalT1} <span className="text-gold-light italic">{c.finalT2}</span></h2>
          <p className="mt-5 text-ivory/75">{c.finalP}</p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/contact" onClick={() => track('start_brand_click', { page: 'private-label-footer' })} data-testid="pl-final-cta" className="inline-flex items-center gap-3 px-12 py-4 bg-gold text-espresso text-xs tracking-[0.2em] uppercase font-semibold hover:bg-gold-light transition-colors">{c.cta1} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" onClick={() => track('whatsapp_click', { page: 'private-label-footer' })} className="inline-flex items-center gap-2 px-10 py-4 border border-ivory/30 text-ivory text-xs tracking-[0.2em] uppercase hover:border-gold-light hover:text-gold-light transition-colors"><MessageCircle className="w-4 h-4" /> {c.waBtn}</a>
          </div>
          <p className="mt-8 text-sm text-ivory/50 flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4 text-gold-light" /> {sh.cert}</p>
        </div>
      </section>
    </div>
  );
}
